import Button from '@salesforce/design-system-react/module/components/button';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import Dropdown from '@salesforce/design-system-react/module/components/menu-dropdown';
import PageHeader from '@salesforce/design-system-react/module/components/page-header';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AccountSvg from '../../assets/accounts';
import {
  createNotes,
  defiTransactionsSelector,
  getDefiTransactions,
  updateNotes,
} from '../../store/defi/transactionsSlice';
import { getDateTimeFromTimestamp, getTimeIntervalText } from '../../utilities/datetime';
import { getNumbersWithDecimalPlaces, getNumberWithCommas } from '../../utilities/money';
import debounce from 'lodash.debounce';
import { PageHeaderControl } from '@salesforce/design-system-react';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import { defiPageOptions } from '../../constants/defiAccount';
import toast, { Toaster } from 'react-hot-toast';
import { isEmpty } from '@firebase/util';
import { resolveAddress } from '../../services/contactAndContract';
import WalletAddressEllipsis from '../../components/shared/WalletAddressEllipsis';
import Tooltip from '@salesforce/design-system-react/components/tooltip';

const CustomDataTableName = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      <Link to={`/transaction/${item.tx_hash}`}>
        <WalletAddressEllipsis text={children.replace(/<[^>]*>?/gm, '')} maxTextCount={20} />
      </Link>
    </DataTableCell>
  );
};
CustomDataTableName.displayName = DataTableCell.displayName;

const CustomDataTableCell = ({ children, ...props }) => {
  return (
    <DataTableCell title={`${children}`} {...props}>
      <p>{children ? getDateTimeFromTimestamp(Number(children) * 1000) : ''}</p>
    </DataTableCell>
  );
};

const CustomDataTableNotes = ({ children, ...props }) => {
  const [editedValue, setEditedValue] = useState(children ? children.note : '');
  const dispatch = useDispatch();

  const addUpdateNotesHandler = (values) => {
    dispatch(values?.id ? updateNotes(values) : createNotes(values));
  };

  const onCellEditData = (item, data) => {
    // setEditedValue(item);
    if (item === children.note) return;
    props.setShowToast(true);
    const values = {
      note: {
        note: item,
        entity_id: data?.item?._id,
        type: 'transaction',
      },
      id: children ? children?.id : null,
    };
    addUpdateNotesHandler(values);
  };

  return (
    <DataTableCell
      title={`${children?.note ? children?.note : 'Add note'}`}
      {...props}
      // className={`${!editedValue && 'bg-yellow'}`}
    >
      <IconSettings iconPath="/assets/icons">
        <Input
          iconLeft={
            <InputIcon
              assistiveText={{
                icon: 'Search',
              }}
              name="edit"
              category="utility"
            />
          }
          type="text"
          value={editedValue}
          placeholder="Add note"
          onChange={(e) => {
            setEditedValue(e.target.value);
          }}
          className={`editable-cell-input tablenote`}
          onBlur={(e) => onCellEditData(e.target.value, props)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onCellEditData(e.target.value, props);
            }
          }}
          styleInput={{ border: '0px' }}
        />
      </IconSettings>
      {/* <input
        type="text"
        value={editedValue}
        placeholder="Add note"
        onChange={(e) => {
          setEditedValue(e.target.value);
        }}
        className={`editable-cell-input tablenote ${!editedValue && 'bg-yellow'}`}
        onBlur={(e) => onCellEditData(e.target.value, props)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onCellEditData(e.target.value, props);
          }
        }}
      /> */}
    </DataTableCell>
  );
};
CustomDataTableNotes.displayName = DataTableCell.displayName;

// Custom data table amount cell
const CustomDataTableAmountCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell
      title={getNumberWithCommas(getNumbersWithDecimalPlaces(item.amount, 4))}
      {...props}
    >
      {item?.amount && (
        <span className="slds-m-right_xx-small">
          <img src="/assets/icons/menus/transactions.svg" alt=""></img>
        </span>
      )}

      {item?.amount ? (
        <span style={{ cursor: 'pointer' }}>{`${getNumberWithCommas(
          getNumbersWithDecimalPlaces(item.amount, 4)
        )}`}</span>
      ) : (
        '--'
      )}
    </DataTableCell>
  );
};
CustomDataTableAmountCell.displayName = DataTableCell.displayName;

// Custom data table gas cell
const CustomDataTableGasCell = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      {/* {item.eth_gas_fee && item.usd_gas_fee && (
        <span className="slds-m-right_xx-small">
          <img src="/assets/icons/menus/transactions.svg" alt=""></img>
        </span>
      )} */}

      {item?.eth_gas_fee && item?.usd_gas_fee ? (
        <>{`${getNumbersWithDecimalPlaces(
          item?.eth_gas_fee,
          4
        )} ETH ($${getNumbersWithDecimalPlaces(item?.usd_gas_fee, 2)})`}</>
      ) : (
        '--'
      )}
    </DataTableCell>
  );
};
CustomDataTableGasCell.displayName = DataTableCell.displayName;

const CustomDataTableCellOne = ({ children, ...props }) => {
  const address = children.substring(children.indexOf('0x'));
  return (
    <DataTableCell title={`${address}`} {...props}>
      {address ? (
        <>
          <span className="slds-m-right_xx-small">
            <img src="/assets/icons/menus/transactions.svg" alt=""></img>
          </span>
          <a
            href={`https://debank.com/profile/${address}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            {resolveAddress(address).name ? (
              resolveAddress(address).name
            ) : (
              <WalletAddressEllipsis text={address.replace(/<[^>]*>?/gm, '')} maxTextCount={20} />
            )}
          </a>
        </>
      ) : (
        '--'
      )}
    </DataTableCell>
  );
};
CustomDataTableCellOne.displayName = DataTableCell.displayName;

CustomDataTableCell.displayName = DataTableCell.displayName;

const DataTableAccountColumn = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      <Link to={`/defi/${item.account}`}>
        <span className="slds-m-right_xx-small">
          <AccountSvg color={item?.account_color || '#CCCCCC'} />
        </span>
        {item.account_name ? item?.account_name : children}
      </Link>
    </DataTableCell>
  );
};

DataTableAccountColumn.displayName = DataTableCell.displayName;

const DataTableTypeColumn = ({ children, ...props }) => {
  const { item } = props;
  let type = children || item?.tx?.name;
  type = type ? type[0].toUpperCase() + type.slice(1) : '';

  return (
    <DataTableCell title={type} {...props}>
      {type}
    </DataTableCell>
  );
};

DataTableTypeColumn.displayName = DataTableCell.displayName;

const CustomDataTableChain = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      <div className="slds-m-right_xx-small" style={{ display: 'flex', gap: '5px' }}>
        {item?.chain_logo && <img src={item?.chain_logo || ''} alt="" height="20" width="20"></img>}
        {item?.chain_name || ''}
      </div>
    </DataTableCell>
  );
};
CustomDataTableChain.displayName = DataTableCell.displayName;

const Transactions = () => {
  const dispatch = useDispatch();
  const { transactions, loading, pagination, errors, success, message } =
    useSelector(defiTransactionsSelector);
  const [sortColumn, setSortColumn] = useState('');
  const [sortColumnDirection, setSortColumnDirection] = useState('');
  const [items, setItems] = useState([]);
  // const [accountsDropDown, setAccountsDropDown] = useState('0');
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [paginated, setPaginated] = useState(0);
  const navigate = useNavigate();
  const [APICalledTime, setAPICalledTime] = useState(new Date());
  const [text, setText] = useState('Recently updated');
  const [searchText, setSearchText] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [rows, setRowData] = React.useState('');
  const [query, setQuery] = React.useState('');

  useEffect(() => {
    dispatch(getDefiTransactions());
  }, [dispatch]);

  useEffect(() => {
    setHasMore(true);
    if (transactions.length > 0) {
      setItems(transactions);
    }
    setAPICalledTime(new Date());
    const data = getTimeIntervalText(APICalledTime);
    setText(data);
  }, [transactions, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      const data = getTimeIntervalText(APICalledTime);
      setText(data);
    }, 20000);

    return () => clearInterval(interval);
  }, [APICalledTime]);

  const handleSort = (sortColumn, ...rest) => {
    const { property, sortDirection } = sortColumn;
    setSortColumn(property);
    setSortColumnDirection(sortDirection);
    setItems([]);
    setHasMore(true);
    setIsPageLoading(false);
    setPaginated(0);
    dispatch(getDefiTransactions({ sort_column: property, sort_order: sortDirection }));

    // sorting will actually be handled from api
    // so dispatch sort action in useEffect
    // dispatch(sortAccounte({sortDirection, sortProperty}))
    // and the update the state with sorted accounts lists this will be done by above useEffect
  };

  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      if (pagination?.hasNextPage) {
        setIsPageLoading(true);
        setPaginated(paginated + 1);
        setTimeout(() => {
          if (pagination.pages <= 1 || paginated >= pagination.pages || !pagination.hasNextPage) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }

          if (pagination.currentPage >= pagination.pages) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }
          let url = new URLSearchParams(pagination.nextPageUrl);

          dispatch(
            getDefiTransactions({
              limit: url.get('limit'),
              offset: url.get('offset'),
              q: searchText,
              sort_column: sortColumn,
              sort_order: sortColumnDirection,
            })
          );
          setIsPageLoading(false);
        }, 1000);
      } else {
        setIsPageLoading(false);
        setHasMore(false);
      }
    }
  };

  const refreshTable = () => {
    setHasMore(true);
    setPaginated(0);
    setItems([]);
    if (searchText !== '') {
      searchHandler(searchText);
    } else {
      dispatch(getDefiTransactions());
    }
  };

  // const searchHandler = (value) => {
  //   // setHasMore(false);
  //   // setItems([]);
  //   // setPaginated(0);
  //   setQuery(value);
  //   const results = items.filter((row) => {
  //     const regex = new RegExp(value, "gi");
  //     return row.tx_hash.match(regex);
  //   });
  //   setRowData(results);

  // };
  // const filteredItems = rows.length === 0 && !query ? items : rows;

  const searchHandler = useCallback(
    debounce((values) => {
      setHasMore(true);
      setItems([]);
      setPaginated(0);
      dispatch(
        getDefiTransactions({
          q: values,
        })
      );
      values === '' && getDefiTransactions();
    }, 1000),
    []
  );

  const onSearchText = (value) => {
    setSearchText(value);
    searchHandler(value);
  };

  const controls = () => (
    <React.Fragment>
      <PageHeaderControl>
        <IconSettings iconPath="/assets/icons">
          <div className="">
            <Input
              iconLeft={
                <InputIcon
                  assistiveText={{
                    icon: 'Search',
                  }}
                  name="search"
                  category="utility"
                />
              }
              id="search_text"
              placeholder="Search Transaction"
              value={searchText}
              onChange={(e) => onSearchText(e.target.value)}
              fixedTextRight={
                <Tooltip
                  id="search-tooltip"
                  align="top right"
                  content="Search works on the basis of transaction id, account and chain"
                  position="overflowBoundaryElement"
                />
              }
            />
          </div>
        </IconSettings>
      </PageHeaderControl>
      {/* <PageHeaderControl>
        <Dropdown
          align="right"
          id="page-header-dropdown-object-home-content-right"
          options={[
            { label: 'Add Contact', value: 'addContact', link: '/settings?page=contacts' },
            { label: 'Add Contract', value: 'addContract', link: '/settings?page=contracts' },
            { label: 'Add Condition', value: 'addCondition', link: 'settings?page=conditions' },
          ]}
          onSelect={(value) => {
            window.location.href = value.link;
          }}
        >
          <DropdownTrigger>
            <Button
              assistiveText={{ icon: 'List View Controls' }}
              iconCategory="utility"
              iconName="settings"
              iconVariant="more"
            />
          </DropdownTrigger>
        </Dropdown>
      </PageHeaderControl> */}
      <PageHeaderControl>
        <Button
          assistiveText={{ icon: 'Refresh' }}
          iconCategory="utility"
          iconName="refresh"
          iconVariant="border-filled"
          variant="icon"
          onClick={() => {
            refreshTable();
          }}
        />
      </PageHeaderControl>
    </React.Fragment>
  );

  useEffect(() => {
    if (!showToast) return;
    success &&
      !loading &&
      toast.success(message, {
        id: 'transaction-note-success',
        duration: 3000,
      });

    !success &&
      !loading &&
      !isEmpty(errors) &&
      toast.error(errors, {
        id: 'transaction-note-error',
        duration: 3000,
      });
  }, [success, message, showToast, errors, loading]);

  // if (success && showToast) {
  //   toast.success(message, {
  //     id: 'trasnaction-success',
  //   });
  //   setShowToast(false);
  // }

  // if (!isEmpty(errors) && showToast) {
  //   toast.error(errors, {
  //     id: 'trasnaction-error',
  //   });
  //   setShowToast(false);
  // }

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <div className="pageContent">
        <PageHeader
          icon={<img src="/assets/icons/menus/accounts.svg" alt=""></img>}
          info={`${items && items.length} items • ${text}`}
          joined
          label="Defi Accounts"
          title="All Transactions"
          nameSwitcherDropdown={
            <Dropdown
              assistiveText={{ icon: 'Name Switcher' }}
              buttonClassName="slds-button_icon-small"
              buttonVariant="icon"
              iconCategory="utility"
              iconName="down"
              onSelect={(e) => {
                navigate('/defi/' + e.value);
              }}
              id="page-header-name-switcher-dropdown"
              options={defiPageOptions}
            />
          }
          // onRenderActions={() =>
          //   Actions({
          //     onAddAction: hanldeAccountAdd,
          //     onDropDownSelect: (value) => {
          //       // setAccountsDropDown(value);
          //       window.location.href = value;
          //     },
          //     value: accountsDropDown,
          //     btnLabel: 'Create Rule',
          //     options: [
          //       { label: 'Choose', value: '0' },
          //       { label: 'Add Contact', value: '/settings?page=contacts' },
          //       { label: 'Add Contact/Protocol', value: '/settings?page=contracts' },
          //       // { type: 'divider' },
          //     ],
          //   })
          // }
          onRenderControls={controls}
          truncate
          variant="object-home"
        />

        {/* do not remove this height property, talk to me before removing it */}
        <div style={{ height: `${transactions.length === 0 ? '100%' : 'calc(100vh - 220px)'}` }}>
          <DataTable
            assistiveText={{
              actionsHeader: 'actions',
              columnSort: 'sort this column',
              columnSortedAscending: 'asc',
              columnSortedDescending: 'desc',
              selectAllRows: 'Select all rows',
              selectRow: 'Select this row',
            }}
            fixedHeader
            fixedLayout
            items={items}
            id="TransactionTable"
            onSort={handleSort}
            hasMore={hasMore}
            className="defi-transaction-dataTable"
            onLoadMore={handleLoadMore}
            noRowHover
          >
            <DataTableColumn
              isSorted={sortColumn === 'tx_hash'}
              label="Transaction ID"
              primaryColumn
              property="tx_hash"
              sortable
              sortDirection={sortColumnDirection.tx_hash}
              width="10rem"
            >
              <CustomDataTableName />
            </DataTableColumn>
            <DataTableColumn label="Type" property="cate_id" width="7rem">
              <DataTableTypeColumn />
            </DataTableColumn>
            <DataTableColumn
              label="Account"
              property="account"
              width="8rem"
              sortable
              sortDirection={sortColumnDirection.account}
            >
              <DataTableAccountColumn />
            </DataTableColumn>
            <DataTableColumn property="amount" label="Amount" width="10rem">
              <CustomDataTableAmountCell />
            </DataTableColumn>
            <DataTableColumn
              isSorted={sortColumn === 'address'}
              label="Address/Protocol"
              property="address"
              width="9rem"
            >
              <CustomDataTableCellOne />
            </DataTableColumn>
            <DataTableColumn
              label="Chain"
              property="chain"
              width="9rem"
              sortable
              sortDirection={sortColumnDirection.chain}
            >
              <CustomDataTableChain />
            </DataTableColumn>
            <DataTableColumn label="Gas" property="gas" width="9rem">
              <CustomDataTableGasCell />
            </DataTableColumn>
            <DataTableColumn
              label="Notes"
              property="note"
              width={transactions.length === 0 ? '5rem' : '16rem'}
            >
              <CustomDataTableNotes setShowToast={setShowToast} />
            </DataTableColumn>
            <DataTableColumn
              label="Transaction Date"
              property="time_at"
              width={transactions.length === 0 ? '10rem' : '12rem'}
              sortable
              sortDirection={sortColumnDirection.time_at}
            >
              <CustomDataTableCell />
            </DataTableColumn>
          </DataTable>
          {(transactions.length === 0 || items.length === 0) && !hasMore && (
            <div className="nodatafound">
              <div>
                <img
                  src="/assets/empty-state/empty-folder.png"
                  alt=""
                  style={{ height: '150px' }}
                />
                <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                  Data not available
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Transactions;
